import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

export const MultiSelectFilter = ({ options, selected, onChange, placeholder, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => !search || o.label.toLowerCase().includes(search.toLowerCase()));
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const clearAll = (e) => { e.stopPropagation(); onChange([]); };

  const selectedLabels = selected.map(v => options.find(o => o.value === v)?.label).filter(Boolean);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 outline-none focus:border-[#002FA7] min-w-[130px] max-w-[220px] hover:border-slate-300 transition-colors"
        data-testid={testId}
      >
        <span className="flex-1 text-left truncate">
          {selected.length === 0 ? placeholder :
           selected.length === 1 ? selectedLabels[0] :
           `${selected.length} selected`}
        </span>
        {selected.length > 0 && (
          <X size={12} className="text-slate-400 hover:text-slate-600 shrink-0" onClick={clearAll} />
        )}
        <ChevronDown size={12} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-testid={`${testId}-dropdown`}>
          {options.length > 6 && (
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 text-xs border-b border-slate-100 outline-none sticky top-0 bg-white"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className="max-h-52 overflow-y-auto">
            {selected.length > 0 && (
              <div
                onClick={() => onChange([])}
                className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50 text-slate-400 border-b border-slate-100"
              >
                Clear all
              </div>
            )}
            {filtered.map(o => (
              <div
                key={o.value}
                onClick={() => toggle(o.value)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50 ${selected.includes(o.value) ? 'bg-blue-50' : ''}`}
              >
                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${selected.includes(o.value) ? 'bg-[#002FA7] border-[#002FA7]' : 'border-slate-300'}`}>
                  {selected.includes(o.value) && <Check size={9} className="text-white" />}
                </div>
                {o.dot && <span className={`w-2 h-2 rounded-full shrink-0`} style={{ backgroundColor: o.dot }} />}
                <span className="text-slate-700 truncate">{o.label}</span>
              </div>
            ))}
            {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No results</div>}
          </div>
        </div>
      )}
    </div>
  );
};
